import React from "react";
import { Box } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, FONT_DISPLAY, RADIUS } from "../../../utils/designTokens";

// The item popup's pieces: group headers, Optional/Required tags, option rows
// and their radio / checkbox marks. The top-level groups and the nested
// follow-up groups both draw from here, so every depth looks alike.

// Popup-only greys: the line between groups and an unticked control's ring.
export const GROUP_LINE = "#F0EEE9";
const CONTROL_RING = "#B9B5AD";

/** "+$4.00" for a priced option; null for a free one, which shows no price. */
export function addPriceLabel(price, currencySymbol) {
  const n = parseFloat(price) || 0;
  return n > 0 ? `+${currencySymbol}${n.toFixed(2)}` : null;
}

/** A DOM-safe id from a group path, so a radiogroup can point at its title. */
export const domId = (...parts) => parts.join("-").replace(/[^\w-]/g, "_");

export const groupTitleSx = (compact = false) => ({
  m: 0,
  fontFamily: FONT_DISPLAY,
  fontSize: compact ? 16 : 20,
  fontWeight: 800,
  lineHeight: 1.2,
  letterSpacing: "-0.01em",
  color: C.ink,
  overflowWrap: "anywhere",
});

export const groupSubtitleSx = (compact = false) => ({
  m: 0,
  mt: "4px",
  fontFamily: FONT_BODY,
  fontSize: compact ? 13 : 14,
  lineHeight: 1.4,
  color: C.muted,
});

/** "Optional" / "Required" pill. Red while a required group is unfilled. */
export function StatusTag({ children, error = false }) {
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        p: "4px 10px",
        borderRadius: RADIUS.pill,
        bgcolor: error ? C.redTint : C.surface2,
        color: error ? C.redLink : C.muted,
        fontFamily: FONT_BODY,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

/** Title + subtitle on the left, status tag on the right. */
export function GroupHeader({ titleId, subTitleId, title, subTitle, status, error = false, priceLabel, compact = false }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: compact ? 1.25 : 1.75 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box component={compact ? "h4" : "h3"} id={titleId} sx={groupTitleSx(compact)}>
          {title}
          {priceLabel ? (
            <Box component="span" sx={{ ml: 1, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, letterSpacing: 0, color: C.ink, whiteSpace: "nowrap" }}>
              {priceLabel}
            </Box>
          ) : null}
        </Box>
        {subTitle ? <Box component="p" id={subTitleId} sx={groupSubtitleSx(compact)}>{subTitle}</Box> : null}
      </Box>
      {status ? <StatusTag error={error}>{status}</StatusTag> : null}
    </Box>
  );
}

/** The drawn radio or checkbox inside an option row. Decorative: the row carries the role. */
export function ChoiceMark({ kind, checked, compact = false }) {
  const size = compact ? 18 : 22;
  const isRadio = kind === "radio";
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        flexShrink: 0,
        boxSizing: "border-box",
        width: size,
        height: size,
        borderRadius: isRadio ? "50%" : compact ? "5px" : "6px",
        border: `2px solid ${checked ? C.red : CONTROL_RING}`,
        bgcolor: checked && !isRadio ? C.red : C.bg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "border-color .15s ease, background-color .15s ease",
      }}
    >
      {checked && isRadio ? (
        <Box component="span" sx={{ width: compact ? 8 : 10, height: compact ? 8 : 10, borderRadius: "50%", bgcolor: C.red }} />
      ) : null}
      {checked && !isRadio ? (
        <svg width={compact ? 12 : 14} height={compact ? 12 : 14} viewBox="0 0 24 24" fill="none">
          <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#FFFFFF" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : null}
    </Box>
  );
}

const rowSx = ({ checked, compact, dim }) => ({
  boxSizing: "border-box",
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: compact ? 1.25 : 1.5,
  minHeight: compact ? 44 : 52,
  p: compact ? "6px 12px" : "8px 16px",
  borderRadius: compact ? "12px" : "14px",
  border: `1.5px solid ${checked ? C.red : C.border}`,
  bgcolor: checked ? C.redTint : C.bg,
  opacity: dim ? 0.5 : 1,
  fontFamily: FONT_BODY,
  color: C.ink,
  textAlign: "left",
  transition: "border-color .15s ease, background-color .15s ease, opacity .15s ease",
});

function RowText({ name, note, checked, strike, compact }) {
  return (
    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
      <Box
        component="span"
        sx={{
          display: "block",
          fontSize: compact ? 14 : 15,
          fontWeight: checked ? 700 : 500,
          lineHeight: 1.35,
          textDecoration: strike ? "line-through" : "none",
          overflowWrap: "anywhere",
        }}
      >
        {name}
      </Box>
      {note ? (
        <Box component="span" sx={{ display: "block", mt: "2px", fontSize: 12, fontWeight: 600, lineHeight: 1.3, color: C.muted }}>
          {note}
        </Box>
      ) : null}
    </Box>
  );
}

function RowPrice({ children, checked, strike, compact }) {
  if (!children) return null;
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        fontSize: compact ? 13 : 14,
        fontWeight: 600,
        color: checked ? C.ink : C.muted,
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
        textDecoration: strike ? "line-through" : "none",
      }}
    >
      {children}
    </Box>
  );
}

/**
 * A full-width option row; the whole row toggles the option.
 *   kind     "radio" | "checkbox"
 *   disabled the option is 86'd: dimmed, struck through, not clickable
 *   faded    the group is at its max: dimmed, but a click still reaches
 *            onClick so the customer is told why nothing happened
 */
export function OptionRow({ kind, checked = false, disabled = false, faded = false, onClick, name, note, price, compact = false }) {
  return (
    <Box
      component="button"
      type="button"
      role={kind}
      aria-checked={checked}
      aria-disabled={disabled || faded ? true : undefined}
      disabled={disabled}
      onClick={onClick}
      sx={{
        ...BUTTON_RESET,
        ...rowSx({ checked, compact, dim: disabled || faded }),
        cursor: disabled || faded ? "not-allowed" : "pointer",
        "&:hover": !checked && !disabled && !faded ? { borderColor: C.borderStrong } : {},
        ...FOCUS_RING,
      }}
    >
      <ChoiceMark kind={kind} checked={checked} compact={compact} />
      <RowText name={name} note={note} checked={checked} strike={disabled} compact={compact} />
      <RowPrice checked={checked} strike={disabled} compact={compact}>{price}</RowPrice>
    </Box>
  );
}

const stepBtnSx = (filled, compact) => ({
  ...BUTTON_RESET,
  ...FOCUS_RING,
  position: "relative",
  width: compact ? 28 : 32,
  height: compact ? 28 : 32,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: filled ? C.ink : C.surface2,
  color: filled ? "#FFFFFF" : C.ink,
  transition: "opacity .15s ease",
  // Stretch the hit area without growing the drawn circle.
  "&::before": { content: '""', position: "absolute", inset: -6 },
  "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
  "& svg": { fontSize: compact ? 16 : 18 },
});

/**
 * A row for a group that takes repeats (Extra cheese ×3): name and price on
 * the row, a −/+ stepper on the right instead of a tick.
 */
export function StepperRow({ name, note, price, qty, onAdd, onRemove, canAdd, canRemove, disabled = false, faded = false, compact = false }) {
  const checked = qty > 0;
  return (
    <Box sx={rowSx({ checked, compact, dim: disabled || faded })}>
      <RowText name={name} note={note} checked={checked} strike={disabled} compact={compact} />
      <RowPrice checked={checked} strike={disabled} compact={compact}>{price}</RowPrice>
      <Box role="group" aria-label={`${name} quantity`} sx={{ display: "inline-flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
        <Box component="button" type="button" onClick={onRemove} disabled={!canRemove} aria-label={`Remove one ${name}`} sx={stepBtnSx(false, compact)}>
          <RemoveRoundedIcon />
        </Box>
        <Box
          component="span"
          aria-live="polite"
          sx={{ minWidth: 18, textAlign: "center", fontSize: compact ? 14 : 15, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
        >
          {qty}
        </Box>
        <Box component="button" type="button" onClick={onAdd} disabled={!canAdd} aria-label={`Add one ${name}`} sx={stepBtnSx(true, compact)}>
          <AddRoundedIcon />
        </Box>
      </Box>
    </Box>
  );
}

/** The column of rows under a group header. */
export function OptionList({ role, labelledBy, compact = false, children }) {
  return (
    <Box role={role} aria-labelledby={labelledBy} sx={{ display: "flex", flexDirection: "column", gap: compact ? "6px" : 1 }}>
      {children}
    </Box>
  );
}
